// ─── Language & Layout Types ──────────────────────────────────────────────────

export type Lang = 'es' | 'en'

export const SUPPORTED_LANGS = ['es', 'en'] as const satisfies readonly Lang[]

export const LEGAL_SLUGS = ['notice', 'privacy', 'terms', 'cookies', 'medical-disclaimer'] as const

/**
 * T898 — the legal layout's route id. `app/routes.ts` registers the layout under it and
 * the document meta looks the layout up by it (a 404 keeps the layout's title), so the
 * id has one owner instead of two hand-copied literals.
 */
export const legalLayoutRouteId = (lang: Lang) => `legal-${lang}` as const

export type LayoutType = 'none' | 'mainApp'

// ─── Route Key Types ──────────────────────────────────────────────────────────

export type PublicRouteKey =
  | 'home'
  | 'ritualCore'
  | 'metodo'
  | 'faq'
  | 'diastasis'
  | 'support'
  | 'unsubscribe'
  | 'legal'
  | 'legalDocument'
  | 'guias'
  | 'guiaDetail'
  | 'autoTest'
  | 'bonusDownload'
  | 'testimonialForm'
  | 'status'

export type AcquisitionRouteKey =
  | 'signup'
  | 'resetPassword'
  | 'recovery'
  | 'verifyEmail'
  | 'checkout'
  | 'thankYou'
  | 'onboarding'
  | 'onboardingProduct'
  | 'assessment'
  | 'evaluationPhase'

export type PrivateRouteKey =
  | 'dashboard'
  | 'phaseProgress'
  | 'explore'
  | 'feedbackForm'
  | 'journal'
  | 'journey'
  | 'library'
  | 'freePractices'
  | 'freePractice'
  | 'sessionLibrary'
  | 'module'
  | 'capsule'

export type PlayerRouteKey = 'player'

export type AccountRouteKey = 'profile'

export type RouteKey =
  | PublicRouteKey
  | AcquisitionRouteKey
  | PrivateRouteKey
  | PlayerRouteKey
  | AccountRouteKey

// ─── Route Definition Interface ───────────────────────────────────────────────

/**
 * How the service worker may cache a route (T52 — `_docs/01-core/pwa-contract.md` §3).
 *
 * - `shell`          — the navigation document may be cached (network-first, cache read
 *                      only when the network fails).
 * - `data-whitelist` — behaves as `shell` since T885: loader `.data` responses are never
 *                      cached, because every body carries root's `sessionUser`
 *                      (`isCacheableDataPath`). Kept as a separate value to mark the
 *                      static public content a future data cache could consider.
 * - `none`           — nothing cached; offline lands on the branded fallback.
 *
 * Every private route is `none`, which is not the obvious choice: this app is
 * server-rendered, so a private route's HTML document already contains the member's
 * data. There is no shell/data seam to cut along at the navigation layer — caching the
 * document for the journal caches her journal entries. See the contract for why the
 * narrower promise is deliberate.
 */
export type CacheStrategy = 'shell' | 'data-whitelist' | 'none'

export interface RouteDefinition {
  /** Absolute localized paths including leading slash */
  readonly paths: Record<Lang, `/${string}`>
  /** File path relative to app/, used by routes.ts */
  readonly file: string
  readonly layout: LayoutType
  readonly isPrivate: boolean
  /** Required, with no default — a default is how something private gets cached by omission. */
  readonly cacheStrategy: CacheStrategy
}

export type RouteConfigMap = Readonly<Record<RouteKey, RouteDefinition>>

// ─── Route Configuration Dictionary ──────────────────────────────────────────

export const ROUTE_CONFIG = {
  // ─── Public Routes (No Layout) ─────────────────────────────────────────────
  home: {
    paths: { es: '/es', en: '/en' },
    file: 'routes/($lang)._index.tsx',
    layout: 'none',
    isPrivate: false,
    /** Landing carries pricing/CTA; network-first means cache is read only offline. */
    cacheStrategy: 'shell',
  },
  ritualCore: {
    paths: { es: '/es/ritual-core', en: '/en/ritual-core' },
    file: 'routes/($lang).ritual-core.tsx',
    layout: 'none',
    isPrivate: false,
    /** Sales page — prices must not be served stale while online, so no SWR on its data. */
    cacheStrategy: 'shell',
  },
  /** Canonical brand explainer — "El método" (ES) / "The method" (EN). GEO-cited (T136). */
  metodo: {
    paths: { es: '/es/metodo', en: '/en/method' },
    file: 'routes/($lang).metodo.tsx',
    layout: 'none',
    isPrivate: false,
    /** Static brand explainer. */
    cacheStrategy: 'data-whitelist',
  },
  /** Canonical structured Q&A — same slug both locales. FAQPage JSON-LD source (T136). */
  faq: {
    paths: { es: '/es/faq', en: '/en/faq' },
    file: 'routes/($lang).faq.tsx',
    layout: 'none',
    isPrivate: false,
    /** Static Q&A. */
    cacheStrategy: 'data-whitelist',
  },
  /** Educational page on diastasis after pregnancy — the first page that answers the question an assistant is asked (2026-11, AI positioning). Article + FAQPage JSON-LD. */
  diastasis: {
    paths: { es: '/es/diastasis', en: '/en/diastasis-recti' },
    file: 'routes/($lang).diastasis.tsx',
    layout: 'none',
    isPrivate: false,
    /** Static educational text. */
    cacheStrategy: 'data-whitelist',
  },
  support: {
    paths: { es: '/es/soporte', en: '/en/support' },
    file: 'routes/($lang).support.tsx',
    layout: 'none',
    isPrivate: false,
    /** Carries a form; the document is safe offline, its data is not whitelisted. */
    cacheStrategy: 'shell',
  },
  /** Public status page (T56b) — overall health, component status, 30-day uptime
   * and the incident timeline. Indexable for GEO ("TheFittClub status" queries). */
  status: {
    paths: { es: '/es/estado', en: '/en/status' },
    file: 'routes/($lang).status.tsx',
    layout: 'none',
    isPrivate: false,
    /** A cached status page reading "operational" during an incident is actively misleading — never cache, even offline. */
    cacheStrategy: 'none',
  },
  /** T281 — token-driven marketing unsubscribe landing. Public, `noindex` utility
   * page (no login); GET confirms, POST sets `do_not_contact=true`. */
  unsubscribe: {
    paths: { es: '/es/baja', en: '/en/unsubscribe' },
    file: 'routes/($lang).unsubscribe.tsx',
    layout: 'none',
    isPrivate: false,
    /** Token-driven mutation. */
    cacheStrategy: 'none',
  },
  legal: {
    paths: { es: '/es/legal', en: '/en/legal' },
    file: 'routes/($lang).legal.tsx',
    layout: 'none',
    isPrivate: false,
    /** Static legal texts. */
    cacheStrategy: 'data-whitelist',
  },
  legalDocument: {
    paths: { es: '/es/legal/:slug', en: '/en/legal/:slug' },
    file: 'routes/($lang).legal.$slug.tsx',
    layout: 'none',
    isPrivate: false,
    cacheStrategy: 'data-whitelist',
  },
  /** Lead-magnet guides collection (T72). Growing list of downloadable guides. */
  guias: {
    paths: { es: '/es/guias', en: '/en/guides' },
    file: 'routes/($lang).guias.tsx',
    layout: 'none',
    isPrivate: false,
    /** Lead-magnet collection; a new guide appearing one visit late is harmless. */
    cacheStrategy: 'data-whitelist',
  },
  /** Single guide detail + email-capture download form (T72). */
  guiaDetail: {
    paths: { es: '/es/guias/:slug', en: '/en/guides/:slug' },
    file: 'routes/($lang).guias.$slug.tsx',
    layout: 'none',
    isPrivate: false,
    /** Static guide body. */
    cacheStrategy: 'data-whitelist',
  },
  /** Auto-test lead magnet: 6 questions → capture → in-surface result (T70). */
  autoTest: {
    paths: { es: '/es/auto-test', en: '/en/self-assessment' },
    file: 'routes/($lang).auto-test.tsx',
    layout: 'none',
    isPrivate: false,
    /** Form + lead capture. */
    cacheStrategy: 'shell',
  },
  /**
   * T71 — the guide-download PAGE. Public, `noindex` utility surface (no login); the
   * signed token (BONUS_TOKEN_SECRET) is the only authority. It renders the three states
   * a download can fail in — `invalid`, `expired`, `unavailable` — each with copy a woman
   * can act on, and redirects a good token to the endpoint that serves the bytes.
   *
   * T742 moved those bytes off this route and onto a resource route (`/es/descarga`,
   * `/en/download`, registered in `app/routes.ts`): a loader on a route WITH a component
   * cannot answer with a body. Before that it 302'd to a presigned
   * `*.r2.cloudflarestorage.com` URL, which is the host Spanish ISPs blocked on 2026-09-13
   * (T741). New links address the endpoint directly; this page exists for the 90-day links
   * already sitting in inboxes, and for everything that can go wrong.
   */
  bonusDownload: {
    paths: { es: '/es/tu-guia', en: '/en/your-guide' },
    file: 'routes/($lang).tu-guia.tsx',
    layout: 'none',
    isPrivate: false,
    /** Per-recipient redirect — never cacheable. */
    cacheStrategy: 'none',
  },

  /**
   * T409 — "Tu historia" testimonial capture. Public and login-free BY DESIGN: the
   * legacy cohort has accounts but no passwords, so requiring auth would force every
   * woman through the reset-password flow before she could write a word, and silently
   * convert a testimonial request into a migration event (that is T386's job).
   * `noindex` — invitation-driven capture, same posture as `bonusDownload`.
   */
  testimonialForm: {
    paths: { es: '/es/tu-historia', en: '/en/your-story' },
    file: 'routes/($lang).tu-historia.tsx',
    layout: 'none',
    isPrivate: false,
    /** Carries a form action and a per-submission thank-you state. */
    cacheStrategy: 'none',
  },

  // ─── Acquisition Flow (No Layout) ──────────────────────────────────────────
  signup: {
    paths: { es: '/es/registro', en: '/en/signup' },
    file: 'routes/($lang).signup.tsx',
    layout: 'none',
    isPrivate: false,
    cacheStrategy: 'none',
  },
  resetPassword: {
    paths: { es: '/es/restablecer-contrasena', en: '/en/reset-password' },
    file: 'routes/($lang).reset-password.tsx',
    layout: 'none',
    isPrivate: false,
    cacheStrategy: 'none',
  },
  recovery: {
    paths: { es: '/es/recuperar-acceso', en: '/en/recover-access' },
    file: 'routes/($lang).recovery.tsx',
    layout: 'none',
    isPrivate: false,
    cacheStrategy: 'none',
  },
  verifyEmail: {
    paths: { es: '/es/verificar-email', en: '/en/verify-email' },
    file: 'routes/($lang).verify-email.tsx',
    layout: 'none',
    isPrivate: false,
    cacheStrategy: 'none',
  },
  checkout: {
    paths: { es: '/es/comprar', en: '/en/checkout' },
    file: 'routes/($lang).checkout.tsx',
    layout: 'none',
    isPrivate: false,
    cacheStrategy: 'none',
  },
  thankYou: {
    paths: { es: '/es/gracias', en: '/en/thank-you' },
    file: 'routes/($lang).thank-you.tsx',
    layout: 'none',
    isPrivate: false,
    /** Order confirmation is member-specific. */
    cacheStrategy: 'none',
  },
  onboarding: {
    paths: { es: '/es/bienvenida', en: '/en/onboarding' },
    file: 'routes/($lang).onboarding.tsx',
    layout: 'none',
    isPrivate: false,
    /** Collects Art. 9 health data. */
    cacheStrategy: 'none',
  },
  onboardingProduct: {
    paths: { es: '/es/bienvenida/:productId', en: '/en/onboarding/:productId' },
    file: 'routes/($lang).onboarding.$productId.tsx',
    layout: 'none',
    isPrivate: false,
    cacheStrategy: 'none',
  },
  /**
   * T531 — `isPrivate: true`, corrected from `false`. Both evaluation routes call
   * `requireSessionLoader`, so an anonymous request has always been redirected to
   * sign-up; the flag simply disagreed with the code. Three costs, each measured
   * before the change: `robots.txt` and `ai.txt` derive their disallow list from
   * this flag and so never listed the evaluation surface; the rendered-invariants
   * sweep put it in the ANONYMOUS pass, where it measured the sign-up redirect and
   * filed the result under `routeKey: "assessment"` (the two baseline entries for
   * `span.foot-soon` — a Footer class, on a `layout: 'none'` screen); and the T137
   * GEO gate fired on it, which is why T296, T497 and T531 each had to write a
   * `## GEO baseline` note explaining why it did not apply.
   *
   * Nothing about caching moves: that is `cacheStrategy`, already `'none'`.
   */
  assessment: {
    paths: { es: '/es/evaluacion', en: '/en/assessment' },
    file: 'routes/($lang).assessment.tsx',
    layout: 'none',
    isPrivate: true,
    /** Collects Art. 9 health data. */
    cacheStrategy: 'none',
  },
  evaluationPhase: {
    paths: { es: '/es/evaluacion/:phaseId', en: '/en/assessment/:phaseId' },
    file: 'routes/($lang).evaluation.$phaseId.tsx',
    layout: 'none',
    isPrivate: true,
    cacheStrategy: 'none',
  },

  // ─── Private Area (MainAppLayout) ──────────────────────────────────────────
  /** Authenticated home — nav label "My Space" / "Mi espacio". URLs stay `/en/dashboard`, `/es/panel`. */
  dashboard: {
    paths: { es: '/es/panel', en: '/en/dashboard' },
    file: 'routes/($lang).dashboard.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  /** T521 — «¿Cómo voy en mi fase?»: the T536 phase-progress screen. */
  phaseProgress: {
    paths: { es: '/es/panel/fase', en: '/en/dashboard/phase' },
    file: 'routes/($lang).phase-progress.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  explore: {
    paths: { es: '/es/panel/explorar', en: '/en/dashboard/explore' },
    file: 'routes/($lang).explore.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  /** T913 — «Mi recorrido»: the Ritual Core journey, one screen under My Space. */
  journey: {
    paths: { es: '/es/panel/recorrido', en: '/en/dashboard/path' },
    file: 'routes/($lang).journey.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  journal: {
    paths: { es: '/es/panel/diario', en: '/en/dashboard/journal' },
    file: 'routes/($lang).journal.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  /**
   * T418 — the beta feedback form. PRIVATE, and that is a product decision rather
   * than a technical one (founder call 2026-08-05): a private route means `user_id` is
   * known, so nothing has to ask her for an email, an identity or a contact consent to
   * attribute an answer. The cost is that a tester who abandoned the app entirely
   * cannot answer — accepted; that case is a direct message, not a form.
   *
   * Being private is also why there is no `.meta.ts` and no `## GEO baseline` (T137).
   */
  feedbackForm: {
    paths: { es: '/es/tu-opinion', en: '/en/your-feedback' },
    file: 'routes/($lang).tu-opinion.tsx',
    layout: 'mainApp',
    isPrivate: true,
    /** Carries a form action and a per-submission thank-you state. */
    cacheStrategy: 'none',
  },
  freePractices: {
    paths: { es: '/es/panel/biblioteca/practicas-libres', en: '/en/dashboard/library/free-practices' },
    file: 'routes/($lang).free-practices.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  freePractice: {
    paths: { es: '/es/panel/biblioteca/practicas-libres/:practiceId', en: '/en/dashboard/library/free-practices/:practiceId' },
    file: 'routes/($lang).free-practice.$practiceId.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  library: {
    paths: { es: '/es/panel/biblioteca', en: '/en/dashboard/library' },
    file: 'routes/($lang).library.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  /** Completed session history (filters + list) — nested under My Space. */
  sessionLibrary: {
    paths: { es: '/es/panel/historial', en: '/en/dashboard/history' },
    file: 'routes/($lang).session-library.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  module: {
    paths: { es: '/es/panel/modulo/:id', en: '/en/dashboard/module/:id' },
    file: 'routes/($lang).module.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
  capsule: {
    paths: { es: '/es/panel/capsula/:id', en: '/en/dashboard/capsule/:id' },
    file: 'routes/($lang).capsule.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },

  // ─── Player (MainAppLayout — Navbar/Footer from root) ───────────────────────
  player: {
    paths: { es: '/es/panel/reproductor/:slug', en: '/en/dashboard/player/:slug' },
    file: 'routes/($lang).player.$slug.tsx',
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },

  // ─── Account Management (SettingsLayout) ───────────────────────────────────
  /** Account / settings — nav label "Profile" / "Perfil". */
  profile: {
    paths: { es: '/es/perfil', en: '/en/profile' },
    file: 'routes/($lang).profile.tsx',
    // T103: the account/profile surface owns its full shell (sub-header +
    // identity credential + section sidebar) per the handoff design, so it no
    // longer mounts under the shared settings-layout chrome (Perfil|Pagos tabs).
    // It uses the bare `mainApp` wrapper (root.tsx still provides Navbar+Footer)
    // — NOT `none`: the `main-app-layout` flex wrapper carries `min-w-0`, which
    // lets the surface's own `overflow-x:auto` section strip contain itself
    // instead of forcing page-wide horizontal scroll on mobile.
    // Privacy is still enforced by the loader's requireSession gate.
    layout: 'mainApp',
    isPrivate: true,
    cacheStrategy: 'none',
  },
} as const satisfies RouteConfigMap

// ─── Utility Types ────────────────────────────────────────────────────────────

/**
 * Recursively extracts dynamic segment names from a path string literal.
 * ExtractParams<'/es/panel/modulo/:id'> → 'id'
 * ExtractParams<'/es/panel'> → never
 */
type ExtractParams<T extends string> =
  T extends `${string}:${infer Param}/${infer Rest}`
    ? Param | ExtractParams<`/${Rest}`>
    : T extends `${string}:${infer Param}`
      ? Param
      : never

/**
 * Params required for a specific route + language combination.
 * Resolves to `undefined` for static routes, `Record<ParamName, string>` for dynamic ones.
 */
export type RouteParams<K extends RouteKey, L extends Lang> =
  ExtractParams<(typeof ROUTE_CONFIG)[K]['paths'][L]> extends never
    ? undefined
    : Record<ExtractParams<(typeof ROUTE_CONFIG)[K]['paths'][L]>, string>

// ─── Path Builder ─────────────────────────────────────────────────────────────

/**
 * Locale of a localized pathname. Exact on the segment ("/en" or "/en/…"), so a
 * future "/energia" route cannot read as English. `$lang` is NOT a route param
 * on every localized match (the EN/ES path shapes differ), so pathname is the
 * reliable source (T498; same idiom as `($lang).onboarding.tsx`).
 */
export function langFromPathname(pathname: string): Lang {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'es'
}

/**
 * Returns the localized absolute path for a route, substituting any dynamic
 * segments (e.g. ':id') with the provided params values.
 */
export function buildLocalizedPath(
  routeKey: RouteKey,
  lang: Lang,
  params?: Record<string, string>,
): string {
  let path: string = ROUTE_CONFIG[routeKey].paths[lang]
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`:${key}`, encodeURIComponent(value))
    }
  }
  return path
}

/** Player URL with optional modality and addon `productId` (modules/capsules). */
export function buildLocalizedPlayerPath(
  lang: Lang,
  slug: string,
  opts?: { modality?: string; productId?: string },
): string {
  const path = buildLocalizedPath("player", lang, { slug })
  const sp = new URLSearchParams()
  if (opts?.modality) sp.set("modality", opts.modality)
  if (opts?.productId) sp.set("productId", opts.productId)
  const q = sp.toString()
  return q ? `${path}?${q}` : path
}

/**
 * Returns the canonical (ES) path for a route key.
 * Useful for alternate-hreflang link generation.
 */
export function getAlternatePaths(routeKey: RouteKey): Record<Lang, string> {
  return { ...ROUTE_CONFIG[routeKey].paths }
}
